import { DEFAULT_FEATURE_FLAGS } from "../config/featureFlags";

/**
 * Maps UI ids — sidebar nav ids, Main.js `activeId` values, and in-page
 * tab ids (HrDashboard / GmDashboard / ReportsHub) — to the single
 * feature flag key each one unambiguously belongs to.
 *
 * Deliberately conservative: an id that mixes more than one module
 * (e.g. "home", "dashboard", "approvals", "team-approvals", "reports",
 * "advance", "wfh", "requests") is NOT listed here on purpose. Hiding a
 * mixed-content screen under a single flag would be a real behavior
 * change, not just "visibility only" — so any id not in this map is
 * always treated as visible, regardless of flags.
 */
export const MODULE_ID_FEATURE_MAP = {
  // leave
  leave: "leave",
  "leave-grant": "leave",
  leaves: "leave", // HrDashboard / GmDashboard / ReportsHub tab id

  // attendance (mirrors the src/pages/attendance/ folder boundary)
  attendance: "attendance",
  "my-days": "attendance",
  "work-mode": "attendance",
  "flexible-manual-attendance": "attendance",
  "kmt-upload": "attendance",
  "attendance-summary": "attendance",
  "location-trail": "attendance",
  "attendance-daily": "attendance",
  "branch-timesheet": "attendance",
  "branch-shift-hours": "attendance",
  "custom-schedule": "attendance",
  "transport-lines": "attendance",
  "attendance-regulations": "attendance",
  "inventory-night": "attendance",

  // overtime
  overtime: "overtime",
  "overtime-grant": "overtime",

  // payroll (mirrors the src/pages/payroll/ folder boundary)
  "kmt-payroll": "payroll",
  "sales-targets": "payroll",
  "my-payslip": "payroll",
  payroll: "payroll", // ReportsHub tab id

  // complaints
  "my-complaints": "complaints",
  "received-complaints": "complaints",
  investigations: "complaints",
  complaints: "complaints", // HrDashboard / GmDashboard / ReportsHub tab id

  // evaluations
  evaluations: "evaluations",
  "evaluations-manage": "evaluations",
  "evaluations-results": "evaluations",

  // recruitment
  recruitment: "recruitment",
  "internal-jobs": "recruitment",
};

/**
 * True unless `flags` explicitly disables `featureKey` (`=== false`).
 * Fail-open: a falsy/unknown featureKey, or a missing flags object,
 * is always visible.
 */
export function isFeatureVisible(flags, featureKey) {
  if (!featureKey) return true;
  const source = flags || DEFAULT_FEATURE_FLAGS;
  return source[featureKey] !== false;
}

/**
 * True unless `navId` maps (via MODULE_ID_FEATURE_MAP) to a feature
 * flag that is explicitly disabled. Any navId with no entry in the map
 * — ambiguous or module-agnostic ids — is always visible, by design.
 */
export function isNavItemVisible(flags, navId) {
  return isFeatureVisible(flags, MODULE_ID_FEATURE_MAP[navId]);
}

/**
 * Route-protection counterpart of isFeatureVisible: same fail-open
 * rule (unknown feature / missing flags => accessible), used at the
 * navigation/routing layer (e.g. Main.js, FeatureGate) to decide
 * whether a screen may actually be rendered — as opposed to
 * isFeatureVisible/isNavItemVisible, which only decide whether a menu
 * entry/shortcut is shown. Kept as a thin alias on purpose so both
 * layers share one source of truth instead of duplicating the check.
 */
export function isFeatureAccessible(flags, featureKey) {
  return isFeatureVisible(flags, featureKey);
}
